/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Grid,
  Paper,
  Button,
  Link as StyledLink,
  withWidth,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MaterialTable, { MTableToolbar } from 'material-table';
import PropTypes from 'prop-types';
import { Preloader } from '..';

const handleScroll = () => {
  window.scrollTo(0, 0);
};

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },
  eventNav: {
    margin: theme.spacing(1),
    marginLeft: theme.spacing(0),
    padding: theme.spacing(1),
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      marginTop: '-55px',
    },
  },
  [theme.breakpoints.down('xs')]: {
    '@global': {
      '.MuiTableFooter-root': {
        '& .MuiIconButton-root': {
          padding: '5px',
        },
        '& .MuiInputBase-input': {
          fontSize: '.65em',
        },
        '& .MuiTypography-root': {
          fontSize: '.65em',
        },
      },
      '#full-width-tabpanel-0': {},
    },
  },
  table: {
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0),
      margin: theme.spacing(0),
    },
    '& div': {
      '& div': {
        '& table': {
          // width: '50%'
        },
      },
    },
  },
  navButton: {
    [theme.breakpoints.down('xs')]: {
      fontSize: '.7em',
    },
  },
}));

const styles = {
  event: {
    minWidth: '350px',
  },
  table: {
    margin: '16px',
    padding: '0',
  },
  mobileCell: {
    fontSize: '.85em',
    paddingBottom: '5px',
  },
};

const columns = [
  {
    title: 'Date',
    field: 'date',
    render: (rowData) => (
      <>
        <div>{rowData.date}</div>
        <div>{rowData.time}</div>
      </>
    ),
  },
  {
    title: 'Event',
    field: 'title',
    render: (rowData) => (
      <div style={styles.event}>
        <StyledLink
          href={rowData.link1}
          target="_blank"
          rel="noopener noreferrer"
        >
          {rowData.title}
        </StyledLink>
        <div>{rowData.venue}</div>
      </div>
    ),
  },
  { title: 'Tags', field: 'genres' },
  {
    title: 'Price / Age',
    field: 'price',
    render: (rowData) => {
      let assembled = rowData.price;
      if (rowData.age && rowData.price) {
        assembled = `${rowData.price} / ${rowData.age}`;
      } else if (rowData.age && rowData.price === '') {
        assembled = rowData.age;
      }
      return assembled;
    },
  },
  { title: 'Organizers', field: 'organizer', sorting: false },
  {
    title: 'Venue',
    field: 'venue',
    sorting: false,
    hidden: true,
    searchable: true,
  },
  {
    title: 'Age',
    field: 'age',
    sorting: false,
    hidden: true,
    searchable: true,
  },
];

const columnsMobile = [
  {
    title: 'Venue',
    field: 'venue',
    sorting: false,
    hidden: true,
    searchable: true,
  },
  {
    title: 'Title',
    field: 'title',
    sorting: false,
    hidden: true,
    searchable: true,
  },
  {
    title: 'Tags',
    field: 'genres',
    sorting: false,
    hidden: true,
    searchable: true,
  },
  {
    title: 'Event',
    field: 'date',
    searchable: true,
    render: (rowData) => {
      const date = () => {
        if (rowData.age) {
          return `${rowData.date} | ${rowData.time} | ${rowData.age}`;
        }
        return `${rowData.date} | ${rowData.time}`;
      };

      const organizer = () => {
        if (rowData.organizer) return `Organizer: ${rowData.organizer}`;
        return '';
      };

      return (
        <>
          <div style={styles.mobileCell}>{date()}</div>
          <div style={styles.mobileCell}>
            <StyledLink
              href={rowData.link1}
              target="_blank"
              rel="noopener noreferrer"
            >
              <h3>{rowData.title}</h3>
            </StyledLink>
          </div>
          <div style={styles.mobileCell}>{rowData.venue}</div>
          <div style={styles.mobileCell}>{rowData.genres}</div>
          <div style={styles.mobileCell}>{organizer()}</div>
          <div style={styles.mobileCell}>{rowData.price}</div>
        </>
      );
    },
  },
];

function RecurringEventList(props) {
  const [filteredData, setData] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [regionFilter, setRegionFilter] = React.useState();
  const [isError, setIsError] = React.useState(false);
  const tableRef = React.useRef(null);
  const classes = useStyles();
  const { width } = props;

  function responsiveColumns() {
    if (width === 'sm' || width === 'xs') {
      return columnsMobile;
    }

    return columns;
  }

  function isDisabled(e) {
    return e === regionFilter;
  }

  // eslint-disable-next-line
  const searchUpdate = (e) => {
    if (e === '') {
      setRegionFilter();
    }
  };

  const eventFilter = (filter) => () => {
    setRegionFilter(filter);
    tableRef.current.onSearchChange(filter);
  };

  React.useEffect(() => {
    const fetchData = async () => {
      setIsError(false);
      setIsLoading(true);
      props.api
        .get(`/data/recurring_${props.region.slug}.json`)
        .then((response) => {
          setData(response.data);
          setIsLoading(false);
        })
        .catch(() => {
          setIsError(true);
        });
    };
    fetchData();
  }, [props.region.slug, props.api]);

  // eslint-disable-next-line
  const components = {
    Toolbar: (tableProps) => (
      <>
        <MTableToolbar {...tableProps} />
        <Grid container className={classes.eventNav} spacing={1}>
          {props.region.filters.map((filter) => (
            <Grid item key={filter}>
              <Button
                key={filter}
                variant="outlined"
                color="secondary"
                size="small"
                className={classes.navButton}
                onClick={eventFilter(filter)}
                disabled={isDisabled(filter)}
              >
                {filter}
              </Button>
            </Grid>
          ))}
        </Grid>
      </>
    ),
  };

  return (
    <>
      {isLoading ? (
        <Paper className={classes.paper}>
          {isError ? (
            <div>Failed to load data. Please try again later.</div>
          ) : (
            <Preloader />
          )}
        </Paper>
      ) : (
        <MaterialTable
          tableRef={tableRef}
          style={styles.table}
          columns={responsiveColumns()}
          data={filteredData}
          onSearchChange={searchUpdate}
          options={props.options}
          icons={props.icons}
          components={components}
          onPageChange={handleScroll}
          onRowsPerPageChange={handleScroll}
          title=""
          className={classes.table}
        />
      )}
    </>
  );
}

RecurringEventList.propTypes = {
  region: PropTypes.object,
  api: PropTypes.func,
  icons: PropTypes.object,
  options: PropTypes.object,
  width: PropTypes.oneOf(['lg', 'md', 'sm', 'xl', 'xs']).isRequired,
};

export default withWidth()(RecurringEventList);
